import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {FaApple, FaGooglePlay, FaMobileAlt} from 'react-icons/fa';
import {useLocation} from 'react-router-dom';

import {useI18n} from '../../i18n';
import useWindowSize from '../../hooks/useWindowSize';
import API from '../../helpers/network/http';
import APP_STORE_LINKS from '../../globals/constants/app_store_links';
import vocalCentricLogo from '../../assets/svgs/vocal_centric_white_logo.svg';
import {EventNames, trackEvent} from '../../analytics';
import {signupAttributionFromSearch} from '../../features/auth/signupAttribution';
import {authenticatedResourceReturnPath} from '../../pages/public/resourceHandoffPaths';

const MOBILE_APP_PROMPT_DISMISSED_KEY = 'vc_mobile_app_prompt_dismissed';
const OPEN_APP_TIMEOUT_MS = 2500;
const DEFERRED_LINK_TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HTTPS_APP_LINK_TARGETS = new Set([
    'home', 'dashboard', 'signup', 'register', 'choir', 'choirs',
    'choir-performance', 'challenge', 'challenges', 'community', 'reel',
    'reels', 'choir-harmonies', 'practice-review', 'rehearsal', 'rehearsals',
    'setlist', 'setlists', 'resource', 'resources', 'choir-setlist',
    'choir-setlists', 'public-setlist', 'public-setlists',
    'choir-join-requests', 'project', 'projects', 'profile', 'contribution',
    'contributions', 'song-requests', 'creators-hub', 'upload-song',
    'accept-choir-invite', 'accept-teaching-invite', 'training-assignment',
    'training-session', 'songs',
]);

const decodePathSegment = (value: string) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const isMobileUserAgent = () => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

    const ua = navigator.userAgent || '';
    const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua) || isiPadOS;
};

const isIosUserAgent = () => {
    if (typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || '';
    return /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const deepLinkWithQuery = (target: string, parameters: URLSearchParams) => {
    const query = parameters.toString();
    return `vocalcentric://${target}${query ? `?${query}` : ''}`;
};

export const canonicalDestinationForDeepLink = (deepLink: string) => {
    try {
        const uri = new URL(deepLink);
        if (uri.protocol !== 'vocalcentric:' || !HTTPS_APP_LINK_TARGETS.has(uri.hostname)) return null;
        const destination = new URL(`https://app.vocalcentric.com/open/${uri.hostname}`);
        uri.searchParams.forEach((value, key) => destination.searchParams.append(key, value));
        return destination.toString();
    } catch {
        return null;
    }
};

export const iosDeferredClipboardPayload = (token: string) => `vc-link:${token}`;

export const playStoreWithDeferredToken = (storeUrl: string, token: string) => {
    const uri = new URL(storeUrl);
    uri.searchParams.set('referrer', new URLSearchParams({vc_link: token}).toString());
    return uri.toString();
};

export const deepLinkHandoffAnalyticsProperties = (deepLink: string | null) => {
    if (!deepLink) return {target_type: 'unknown', has_referral: false};
    try {
        const uri = new URL(deepLink);
        const referralCode = uri.searchParams.get('referral')?.trim();
        const attribution = signupAttributionFromSearch(uri.search);
        const campaignProperties = Object.fromEntries(
            Object.entries(attribution ?? {}).map(([key, value]) => [
                key === 'source' ? 'campaign_source' : key,
                value,
            ]),
        );
        return {
            target_type: uri.hostname || 'unknown',
            has_referral: Boolean(referralCode),
            has_campaign_attribution: Object.keys(campaignProperties).length > 0,
            ...(referralCode ? {referral_code: referralCode} : {}),
            ...campaignProperties,
        };
    } catch {
        return {target_type: 'unknown', has_referral: false};
    }
};

export const prepareStoreHandoff = async ({
    key,
    storeUrl,
    token,
    isIos,
    writeClipboard,
}: {
    key: string;
    storeUrl: string;
    token: string | null;
    isIos: boolean;
    writeClipboard?: (value: string) => Promise<void>;
}) => {
    if (!token) return storeUrl;
    if (key === 'playStore') return playStoreWithDeferredToken(storeUrl, token);
    if (key === 'appStore' && isIos && writeClipboard) {
        try {
            await writeClipboard(iosDeferredClipboardPayload(token));
        } catch {
            // The App Store remains available when clipboard access fails.
        }
    }
    return storeUrl;
};

export const getCurrentPageDeepLink = (location?: {pathname: string; search: string}) => {
    if (typeof window === 'undefined') return null;

    const current = location ?? window.location;
    const returnTo = authenticatedResourceReturnPath(new URLSearchParams(current.search).get('returnTo'));
    const {pathname, search} = returnTo
        ? new URL(returnTo, window.location.origin)
        : current;
    const searchParams = new URLSearchParams(search);

    const openMatch = pathname.match(/^\/open\/([a-z0-9-]+)\/?$/);
    if (openMatch?.[1] && HTTPS_APP_LINK_TARGETS.has(openMatch[1])) {
        searchParams.delete('vc_web');
        return deepLinkWithQuery(openMatch[1], searchParams);
    }

    if (pathname === '/share/accept-teaching-invitation') {
        const token = searchParams.get('token');
        if (!token) return null;

        searchParams.delete('vc_web');
        return deepLinkWithQuery('accept-teaching-invite', searchParams);
    }

    if (pathname === '/accept-choir-invitation' || pathname === '/share/accept-choir-invitation') {
        const token = searchParams.get('token');
        if (!token) return null;

        searchParams.delete('vc_web');
        return deepLinkWithQuery('accept-choir-invite', searchParams);
    }

    const setlistMatch = pathname.match(/^\/(?:pub-share|share)\/setlist\/([^/]+)/);
    if (setlistMatch?.[1]) {
        searchParams.delete('vc_web');
        const setlistType = searchParams.get('setlist_type') === 'choir' ? 'choir' : 'personal';
        searchParams.set('id', decodePathSegment(setlistMatch[1]));
        searchParams.set('setlist_type', setlistType);

        return deepLinkWithQuery('public-setlists', searchParams);
    }

    if (pathname === '/pub-share/harmonies' || pathname === '/share/harmonies') {
        const token = searchParams.get('token');
        if (!token) return null;

        searchParams.delete('vc_web');
        return deepLinkWithQuery('choir-harmonies', searchParams);
    }

    const communityThreadMatch = pathname.match(/^\/share\/community\/([^/]+)\/?$/);
    if (communityThreadMatch?.[1]) {
        searchParams.delete('vc_web');
        searchParams.set('post_uuid', decodePathSegment(communityThreadMatch[1]));
        return deepLinkWithQuery('community', searchParams);
    }

    const resourceMatch = pathname.match(/^\/share\/resources\/([^/]+)\/?$/);
    if (resourceMatch?.[1]) {
        searchParams.delete('vc_web');
        searchParams.set('id', decodePathSegment(resourceMatch[1]));
        return deepLinkWithQuery('resource', searchParams);
    }

    const shareMatch = pathname.match(
        /^\/share\/(reels|songs|choirs|challenges|projects|profiles)\/([^/]+)\/?$/,
    );
    if (shareMatch?.[1] && shareMatch[2]) {
        const target = shareMatch[1] === 'profiles' ? 'profile' : shareMatch[1];
        const parameter = target === 'profile' ? 'username' : 'id';
        searchParams.delete('vc_web');
        searchParams.set(parameter, decodePathSegment(shareMatch[2]));
        return deepLinkWithQuery(target, searchParams);
    }

    const choirMatch = pathname.match(/^\/choirs\/([^/]+)/);
    if (choirMatch?.[1]) {
        searchParams.delete('vc_web');
        searchParams.set('id', decodePathSegment(choirMatch[1]));
        return deepLinkWithQuery('choirs', searchParams);
    }

    return null;
};

const openDeepLink = (deepLink: string, onFallback?: () => void) => {
    let appOpened = false;
    const markAppOpened = () => {
        appOpened = true;
    };
    const markHidden = () => {
        if (document.hidden) markAppOpened();
    };
    const cleanup = () => {
        document.removeEventListener('visibilitychange', markHidden);
        window.removeEventListener('pagehide', markAppOpened);
    };

    document.addEventListener('visibilitychange', markHidden);
    window.addEventListener('pagehide', markAppOpened);
    window.location.href = deepLink;

    const timeoutId = window.setTimeout(() => {
        cleanup();
        if (!appOpened && !document.hidden) {
            onFallback?.();
        }
    }, OPEN_APP_TIMEOUT_MS);

    return () => {
        window.clearTimeout(timeoutId);
        cleanup();
    };
};

const MobileAppRequiredModal = () => {
    const {t} = useI18n();
    const location = useLocation();
    const {isMobile} = useWindowSize();
    const [isMobileBrowser, setIsMobileBrowser] = useState(false);
    const [appOpenAttemptComplete, setAppOpenAttemptComplete] = useState(false);
    const [deferredToken, setDeferredToken] = useState<string | null>(null);
    const [storeHandoffPrepared, setStoreHandoffPrepared] = useState(false);
    const promptTrackedForLink = useRef<string | null>(null);
    const [dismissed, setDismissed] = useState(() => {
        if (typeof window === 'undefined') return false;
        try {
            return window.sessionStorage.getItem(MOBILE_APP_PROMPT_DISMISSED_KEY) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        setIsMobileBrowser(isMobileUserAgent());
    }, []);

    const deepLink = getCurrentPageDeepLink(location);
    const isMobileExperience = isMobile || isMobileBrowser;

    useEffect(() => {
        let active = true;
        const destination = deepLink ? canonicalDestinationForDeepLink(deepLink) : null;
        if (!isMobileExperience || !destination) {
            setDeferredToken(null);
            setStoreHandoffPrepared(true);
            return () => {
                active = false;
            };
        }

        setDeferredToken(null);
        setStoreHandoffPrepared(false);
        const prepare = async () => {
            for (let attempt = 0; attempt < 3 && active; attempt++) {
                try {
                    const response = await API.post('/deferred-links', {destination}, {timeout: OPEN_APP_TIMEOUT_MS});
                    const token = response.data?.data?.token;
                    if (active && typeof token === 'string' && DEFERRED_LINK_TOKEN_PATTERN.test(token)) {
                        setDeferredToken(token);
                        break;
                    }
                } catch {
                    // Retry temporary failures before offering the store handoff.
                }
                if (attempt < 2 && active) await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
            }
            if (active) setStoreHandoffPrepared(true);
        };
        void prepare();

        return () => {
            active = false;
        };
    }, [deepLink, isMobileExperience]);

    useEffect(() => {
        if (!isMobileExperience || dismissed || !deepLink) {
            setAppOpenAttemptComplete(true);
            return;
        }

        setAppOpenAttemptComplete(false);
        trackEvent(EventNames.APP_OPEN_ATTEMPTED, {
            ...deepLinkHandoffAnalyticsProperties(deepLink),
            source: 'automatic_mobile_web',
        });
        return openDeepLink(deepLink, () => setAppOpenAttemptComplete(true));
    }, [deepLink, dismissed, isMobileExperience]);

    const dismissPrompt = (dismissMethod: string = 'dialog') => {
        trackEvent(EventNames.MOBILE_APP_PROMPT_DISMISSED, {
            ...deepLinkHandoffAnalyticsProperties(deepLink),
            dismiss_method: dismissMethod,
        });
        setDismissed(true);
        try {
            window.sessionStorage.setItem(MOBILE_APP_PROMPT_DISMISSED_KEY, 'true');
        } catch {
            // Ignore storage failures; the in-memory dismissed state still lets the user continue.
        }
    };

    const openStore = async (
        event: React.MouseEvent<HTMLAnchorElement>,
        key: string,
        storeUrl: string,
    ) => {
        event.preventDefault();
        trackEvent(EventNames.STORE_HANDOFF_STARTED, {
            ...deepLinkHandoffAnalyticsProperties(deepLink),
            store: key === 'appStore' ? 'ios_app_store' : 'google_play',
            has_deferred_token: Boolean(deferredToken),
            handoff_transport: deferredToken
                ? (key === 'appStore' ? 'clipboard' : 'install_referrer')
                : 'none',
        });

        const destination = await prepareStoreHandoff({
            key,
            storeUrl,
            token: deferredToken,
            isIos: isIosUserAgent(),
            writeClipboard: navigator.clipboard?.writeText.bind(navigator.clipboard),
        });

        window.location.href = destination;
    };

    const openApp = () => {
        if (deepLink) {
            trackEvent(EventNames.APP_OPEN_ATTEMPTED, {
                ...deepLinkHandoffAnalyticsProperties(deepLink),
                source: 'mobile_web_prompt',
            });
            openDeepLink(deepLink);
        }
    };

    const storeLinks = useMemo(() => {
        const links = [
            {
                key: 'playStore',
                href: APP_STORE_LINKS.playStore,
                label: t('modals.mobileAppRequired.playStore'),
                icon: <FaGooglePlay aria-hidden="true" className="shrink-0 text-[24px]"/>,
            },
            {
                key: 'appStore',
                href: APP_STORE_LINKS.appStore,
                label: t('modals.mobileAppRequired.appStore'),
                icon: <FaApple aria-hidden="true" className="shrink-0 text-[24px]"/>,
            },
        ];

        return isIosUserAgent() ? links.reverse() : links;
    }, [t]);

    const show = !dismissed
        && isMobileExperience
        && (!deepLink || (appOpenAttemptComplete && storeHandoffPrepared));

    useEffect(() => {
        if (!show) return;
        const trackingKey = deepLink ?? window.location.pathname;
        if (promptTrackedForLink.current === trackingKey) return;
        promptTrackedForLink.current = trackingKey;
        trackEvent(EventNames.MOBILE_APP_PROMPT_SHOWN, {
            ...deepLinkHandoffAnalyticsProperties(deepLink),
            has_deferred_token: Boolean(deferredToken),
        });
    }, [deepLink, deferredToken, show]);

    return (
        <Transition appear show={show} as={Fragment}>
            <Dialog as="div" className="relative z-[10000]" onClose={() => dismissPrompt('dialog')}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-150"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/90 backdrop-blur-md"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full flex-col items-center justify-center px-6 py-8 text-white">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-3"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-3"
                        >
                            <Dialog.Panel className="flex min-h-[calc(100vh-64px)] w-full max-w-md flex-col items-center justify-center text-center">
                                <button
                                    type="button"
                                    onClick={() => dismissPrompt('close_button')}
                                    className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white/80 transition hover:bg-white/15 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
                                    aria-label={t('modals.mobileAppRequired.close')}
                                >
                                    &times;
                                </button>

                                <div className="mb-8">
                                    <img src={vocalCentricLogo} alt="VocalCentric" className="mx-auto h-12 w-auto"/>
                                </div>

                                <Dialog.Title className="text-[28px] font-semibold leading-tight text-white">
                                    {t('modals.mobileAppRequired.title')}
                                </Dialog.Title>
                                <Dialog.Description className="mt-4 max-w-xs text-base leading-7 text-white/70">
                                    {t('modals.mobileAppRequired.description')}
                                </Dialog.Description>
                                {deepLink && (
                                    <p className="mt-3 max-w-xs text-sm leading-6 text-white/70">
                                        {t('modals.mobileAppRequired.afterInstall')}
                                    </p>
                                )}

                                <div className="mt-7 flex w-full flex-col items-center gap-2.5">
                                    {deepLink && (
                                        <button
                                            type="button"
                                            onClick={openApp}
                                            className="button button_primary button_long !mb-0 inline-flex min-h-[46px] w-full max-w-[320px] items-center justify-center gap-3 !px-5 !py-2.5 !text-sm focus:outline-none focus:ring-2 focus:ring-ORANGE/60"
                                        >
                                            <FaMobileAlt aria-hidden="true" className="shrink-0 text-[22px]"/>
                                            <span>{t('modals.mobileAppRequired.openApp')}</span>
                                        </button>
                                    )}
                                    {storeLinks.map((link) => (
                                        <a
                                            key={link.key}
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(event) => openStore(event, link.key, link.href)}
                                            className="button button_primary button_long !mb-0 inline-flex min-h-[46px] w-full max-w-[320px] items-center justify-center gap-3 !px-5 !py-2.5 !text-sm focus:outline-none focus:ring-2 focus:ring-ORANGE/60"
                                        >
                                            {link.icon}
                                            <span>{link.label}</span>
                                        </a>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={() => dismissPrompt('continue_web')}
                                    className="mt-5 text-sm font-semibold text-ORANGE transition hover:text-ORANGE/80 focus:outline-none focus:ring-2 focus:ring-ORANGE/50"
                                >
                                    {t('modals.mobileAppRequired.continueWeb')}
                                </button>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default MobileAppRequiredModal;
